import React from 'react';
import { styled } from '@mui/system';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';

export type FileUploaderProps = {
  name: string;
  value?: File[];
  onChange: (value: File[]) => void;
  label: string;
  disabled?: boolean;
  inputProps?: {
    accept?: string;
    multiple?: boolean;
  },
  buttonRender?: (props: LoadingButtonProps) => React.ReactElement;
  buttonProps?: Omit<LoadingButtonProps, 'disabled'>;
  validator?: (file: File) => boolean;
  onFilesRejected?: (files: File[]) => void;
}

const HiddenInput = styled('input')({
  display: 'none',
});

export const FileUploader: React.FC<FileUploaderProps> = (props) => {
  const { name, label, disabled, onChange, inputProps, buttonRender, buttonProps, validator, onFilesRejected } = props;

  const handleFileInput = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !event.target.files) return;

    const files = Array.from(event.target.files);
    const validFiles: File[] = [];
    const rejectedFiles: File[] = [];

    for (const file of files) {
      if (!validator || validator(file)) {
        validFiles.push(file);
      } else {
        rejectedFiles.push(file);
      }
    }

    onChange(validFiles);
    onFilesRejected?.(rejectedFiles);
  }, [disabled, onChange, validator, onFilesRejected]);

  return (
    <React.Fragment>
      <HiddenInput
        id={name}
        type="file"
        onChange={handleFileInput}
        value=""
        disabled={disabled}
        {...inputProps}
      />
      <label htmlFor={name}>
        {!!buttonRender ? buttonRender({
          ...buttonProps,
          disabled,
        }) : (
          <LoadingButton disabled={disabled} {...buttonProps} component="span">
            {label}
          </LoadingButton>
        )}
      </label>
    </React.Fragment>
  );
};
